// Globals
import React, { ChangeEvent, useMemo, memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import dayjs, { Dayjs } from 'dayjs';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getUsersList } from '../../feature';
import { selectUserQuery } from '../../feature/selectors';
import { changeUserQuery } from '../../feature/slice';

// Modules
import { usePermissions, Permission, Resource } from '@/modules/Permissions';

// Components
import { Input, Select } from '@/ui';
import { StyledDatePicker, StyledFilterRow } from '../../components';

// Helpers
import { getStatusOptions } from '../../helpers/getOptions';
import { GetUsersListDTO } from '../../dtos';
import { UsersFiltersFields } from '../../helpers/types';

type IUsersFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const UsersFiltersContainer = ({ children, className }: IUsersFiltersContainer) => {
  const { t } = useTranslation(['usersList', 'common']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectUserQuery);
  const { can, canManage } = usePermissions();
  // Keep the phone-search column aligned with the (conditionally rendered) contacts column.
  const canViewContacts = can(Permission.SITE_USER_VIEW_CONTACTS);
  // The trailing actions column only exists when the admin can manage site users.
  const canManageUsers = canManage(Resource.SITE_USER);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetUsersListDTO(searchParams);
        dispatch(getUsersList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined, page: 1 };
    dispatch(changeUserQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleStatusFilter = (value?: string) => {
    const newQuery = {
      ...query,
      page: 1,
      [UsersFiltersFields.ACTIVE]: value,
    };
    dispatch(changeUserQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateChange = (date: Dayjs) => {
    const newQuery = {
      ...query,
      page: 1,
      [UsersFiltersFields.CREATED_AT]: date ? dayjs(date).format('YYYY-MM-DD') : undefined,
    };
    dispatch(changeUserQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('users_list_name')}
            name={UsersFiltersFields.NAME}
            onChange={handleSearch}
            value={query?.[UsersFiltersFields.NAME] || ''}
            size="large"
            allowClear
          />
        </th>
        {canViewContacts && (
          <th>
            <Input
              placeholder={t('users_list_phone_number')}
              name={UsersFiltersFields.PHONE}
              onChange={handleSearch}
              value={query?.[UsersFiltersFields.PHONE] || ''}
              size="large"
              allowClear
            />
          </th>
        )}
        <th>
          <StyledDatePicker
            name={UsersFiltersFields.CREATED_AT}
            onChange={handleDateChange}
            value={query?.[UsersFiltersFields.CREATED_AT] ? dayjs(query[UsersFiltersFields.CREATED_AT]) : null}
            size="middle"
          />
        </th>
        <th>
          <Select
            options={getStatusOptions(t)}
            placeholder={t('users_list_status')}
            onChange={handleStatusFilter}
            value={query?.[UsersFiltersFields.ACTIVE] || undefined}
            size="large"
            allowClear
          />
        </th>
        {canManageUsers && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(UsersFiltersContainer);
